import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button, Container, Paper, useTheme, alpha } from '@mui/material';
import { Home as HomeIcon, Refresh as RefreshIcon, BugReport as BugReportIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component para capturar erros React e exibir página de erro amigável
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log do erro para monitoramento
    console.error('❌ Erro capturado pelo ErrorBoundary:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Chamar callback personalizado se fornecido
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Se há um fallback customizado, usar ele
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Renderizar página de erro padrão
      return <ErrorBoundaryFallback 
        error={this.state.error} 
        errorInfo={this.state.errorInfo}
        onReset={this.handleReset}
        onGoHome={this.handleGoHome}
        onReload={this.handleReload}
      />;
    }

    return this.props.children;
  }
}

interface ErrorBoundaryFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onGoHome: () => void;
  onReload: () => void;
}

/**
 * Componente de fallback padrão para ErrorBoundary
 */
const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({
  error,
  errorInfo,
  onReset,
  onGoHome,
  onReload,
}) => {
  const theme = useTheme();

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        py={4}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            width: '100%',
            maxWidth: 600
          }}
        >
          {/* Ícone de erro */}
          <Box sx={{ mb: 4 }}>
            <BugReportIcon 
              sx={{ 
                fontSize: 100, 
                color: theme.palette.error.main,
                mb: 2,
                animation: 'pulse 2s infinite'
              }} 
            />
          </Box>

          {/* Mensagem principal */}
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2
            }}
          >
            Ops! Algo deu errado
          </Typography>

          <Typography 
            variant="body1" 
            color="text.secondary" 
            paragraph
            sx={{ fontSize: '1.1rem', mb: 4 }}
          >
            Ocorreu um erro inesperado na aplicação. Nossa equipe foi notificada e está trabalhando para resolver o problema.
          </Typography>

          {/* Detalhes do erro (apenas em desenvolvimento) */}
          {process.env.NODE_ENV === 'development' && error && (
            <Box 
              sx={{ 
                mb: 4,
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                borderRadius: 2,
                textAlign: 'left',
                maxHeight: 300,
                overflow: 'auto'
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Detalhes do Erro (Desenvolvimento):
              </Typography>
              <Typography 
                variant="body2" 
                component="pre" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {error.toString()}
                {errorInfo?.componentStack && (
                  <>
                    {'\n\n'}
                    Component Stack:
                    {errorInfo.componentStack}
                  </>
                )}
              </Typography>
            </Box>
          )}

          {/* Botões de ação */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<HomeIcon />}
              onClick={onGoHome}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: theme.shadows[4]
              }}
            >
              Ir para o Início
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={onReload}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Recarregar Página
            </Button>

            {process.env.NODE_ENV === 'development' && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={onReset}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Tentar Novamente
              </Button>
            )}
          </Box>
        </Paper>

        {/* CSS para animação */}
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.1); opacity: 0.8; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}
        </style>
      </Box>
    </Container>
  );
};

export default ErrorBoundary;

